import { useAuthStore } from "@/store/authStore";
import {
  LayoutDashboard,
  Boxes,
  Wrench,
  CalendarCheck,
  Package,
  ShoppingCart,
  FileText,
  Activity,
  BarChart3,
  ScrollText,
  Settings,
  Users,
  ShieldCheck,
  ShieldAlert,
  ArrowLeftRight,
  Ban,
  Recycle,
  PackagePlus,
  PackageCheck,
  Wallet,
  ClipboardCheck,
  SlidersHorizontal,
  AlertTriangle,
  Ticket,
  LayoutTemplate,
  GitCompare,
  ReceiptText,
  GitMerge,
  type LucideIcon,
  Undo2,
} from "lucide-react";

export type AppRole =
  | "super_admin"
  | "biomed_head"
  | "biomed_engineer"
  | "facility_manager"
  | "finance"
  | "purchase_stores"
  | "hod"
  | "dept_user"
  | "quality"
  | "management";

export const ROLE_LABELS: Record<AppRole, string> = {
  super_admin: "Super Admin",
  biomed_head: "Biomedical Head",
  biomed_engineer: "Biomedical Engineer",
  facility_manager: "Facility Manager",
  finance: "Finance",
  purchase_stores: "Purchase / Stores",
  hod: "Department HOD",
  dept_user: "Department User",
  quality: "Quality Coordinator",
  management: "Management",
};

export const formatRoles = (roles?: string[]) => {
  if (!roles || roles.length === 0) return "";
  return roles.map(r => ROLE_LABELS[r as AppRole] || r.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ");
};

export type NavItem = {
  label: string;
  path: string;
  search?: Record<string, string>;
  icon: LucideIcon;
  roles: AppRole[];
  module?: import("@/lib/permissions").Module;
  /** When set, the item is shown only to users holding ANY of these permission keys (Super Admin always). Overrides module / role visibility: no broad view_* key, department role or parent menu can substitute. */
  permissions?: string[];
  group?: string;
};

const ALL: AppRole[] = [
  "super_admin",
  "biomed_head",
  "biomed_engineer",
  "facility_manager",
  "finance",
  "purchase_stores",
  "hod",
  "dept_user",
  "quality",
  "management",
];

// Sidebar items derived from BRD role matrix. Visibility = canOpen (read or
// write). NOTE: write-vs-read enforcement happens in RouteGuard / ActionGuard.
// Management is intentionally EXCLUDED from Work Orders — accessed via
// Dashboard and Reports only.
export const NAV: NavItem[] = [
  // OVERVIEW
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, roles: ALL, module: "dashboard", group: "OVERVIEW" },

  { label: "Assets", path: "/assets", icon: Boxes, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","finance","purchase_stores","dept_user","quality","management"], module: "assets", group: "ASSET MANAGEMENT" },
  // { label: "Integrated Assets", path: "/integrated-assets", icon: GitMerge, roles: ["super_admin"], module: "assets", group: "ASSET MANAGEMENT" },
  { label: "Transfers", path: "/transfers", icon: ArrowLeftRight, roles: ["super_admin","biomed_head","facility_manager","biomed_engineer","purchase_stores","dept_user"], module: "transfers", group: "ASSET MANAGEMENT" },

  // SERVICE & MAINTENANCE
  { label: "Tickets", path: "/tickets", icon: Ticket, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","dept_user","quality","management"], module: "tickets", group: "SERVICE & MAINTENANCE" },
  { label: "Work Orders", path: "/work-orders", icon: Wrench, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","dept_user","quality"], module: "work_orders", group: "SERVICE & MAINTENANCE" },
  { label: "Preventive Maintenance", path: "/preventive-maintenance", icon: CalendarCheck, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","quality"], module: "pm", group: "SERVICE & MAINTENANCE" },
  { label: "My Allocations", path: "/my-allocations", icon: ClipboardCheck, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","purchase_stores","dept_user","quality","management","finance"], module: "assets", group: "SERVICE & MAINTENANCE" },

  // STORE & PROCUREMENT
  { label: "Store Items", path: "/spares", icon: Package, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","purchase_stores"], permissions: ["view_store_items"], group: "STORE & PROCUREMENT" },
  { label: "Store Indents", path: "/store-indents", icon: PackagePlus, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","purchase_stores","dept_user","quality"], module: "store_indents", group: "STORE & PROCUREMENT" },
  { label: "Defective Returns", path: "/store-returns", icon: AlertTriangle, roles: ["super_admin","purchase_stores"], permissions: ["view_defective_returns"], group: "STORE & PROCUREMENT" },
  { label: "Purchase Indents", path: "/indents", icon: ShoppingCart, roles: ["super_admin","biomed_head","facility_manager","purchase_stores","dept_user","finance","management","hod"], module: "indents", group: "STORE & PROCUREMENT" },
  { label: "RFQs & Quotations", path: "/rfqs", icon: ReceiptText, roles: ["super_admin","purchase_stores","finance","management","biomed_head"], module: "rfqs", group: "STORE & PROCUREMENT" },
  { label: "Purchase Orders", path: "/purchase-orders", icon: ShoppingCart, roles: ["super_admin","biomed_head","facility_manager","purchase_stores","finance","dept_user","management","hod"], module: "purchase_orders", group: "STORE & PROCUREMENT" },
  { label: "GRN", path: "/grns", icon: PackageCheck, roles: ["super_admin","biomed_head","facility_manager","purchase_stores"], module: "grns", group: "STORE & PROCUREMENT" },
  { label: "Return to Vendor", path: "/vendor-returns", icon: Undo2, roles: ["super_admin","purchase_stores"], permissions: ["view_vendor_returns"], group: "STORE & PROCUREMENT" },

  // PROCUREMENT CONFIGURATION
  { label: "Quotation Templates", path: "/quotation-templates", icon: LayoutTemplate, roles: ["super_admin","purchase_stores","finance","management"], module: "quotation_templates", group: "PROCUREMENT CONFIGURATION" },
  { label: "Comparison Templates", path: "/quotation-comparison-templates", icon: GitCompare, roles: ["super_admin","purchase_stores","finance","management"], module: "rfqs", group: "PROCUREMENT CONFIGURATION" },

  // CONTRACTS & COMPLIANCE
  { label: "Contracts", path: "/contracts", icon: FileText, roles: ["super_admin","biomed_head","facility_manager","finance","purchase_stores"], module: "contracts", group: "CONTRACTS & COMPLIANCE" },
  { label: "Calibration & QA", path: "/calibration", icon: Activity, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","quality"], module: "calibration", group: "CONTRACTS & COMPLIANCE" },
  { label: "Compliance", path: "/compliance/licences", icon: ShieldCheck, roles: ["super_admin","biomed_head","facility_manager","quality","management"], module: "compliance", group: "CONTRACTS & COMPLIANCE" },
  { label: "Insurance", path: "/insurance", icon: ShieldAlert, roles: ["super_admin","finance","management"], module: "insurance", group: "CONTRACTS & COMPLIANCE" },

  // FINANCE
  { label: "Finance", path: "/finance/dashboard", icon: Wallet, roles: ["super_admin","finance","management"], module: "finance", group: "FINANCE" },

  // ASSET RETIREMENT
  { label: "Condemnation", path: "/condemnation", icon: Ban, roles: ["super_admin","biomed_head","facility_manager","biomed_engineer","finance"], module: "condemnation", group: "ASSET RETIREMENT" },
  { label: "Disposals", path: "/disposals", icon: Recycle, roles: ["super_admin","biomed_head","facility_manager","finance","purchase_stores"], module: "disposals", group: "ASSET RETIREMENT" },

  // GOVERNANCE & ANALYTICS
  { label: "Audits", path: "/audits", icon: ClipboardCheck, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","quality"], module: "audits", group: "GOVERNANCE & ANALYTICS" },
  { label: "Reports", path: "/reports", icon: BarChart3, roles: ["super_admin","biomed_head","biomed_engineer","facility_manager","finance","purchase_stores","dept_user","quality","management"], module: "reports", group: "GOVERNANCE & ANALYTICS" },
  { label: "Audit Logs", path: "/audit-logs", icon: ScrollText, roles: ["super_admin"], module: "audit_logs", group: "GOVERNANCE & ANALYTICS" },

  // SYSTEM
  { label: "Admin", path: "/admin", icon: Settings, roles: ["super_admin","biomed_head","facility_manager"], module: "admin_users", group: "SYSTEM" },
  { label: "Configuration Centre", path: "/configuration", icon: SlidersHorizontal, roles: ["super_admin","management"], module: "configuration", group: "SYSTEM" },
];

export function homePathForRoles(roles: AppRole[]): string {
  return "/dashboard";
}

// BRD: Direct asset creation from the Asset Registry is restricted to
// biomedical/facility admins. purchase_stores and finance create assets
// only through the controlled GRN → Asset flow (see canCreateAssetFromGRN).
export const ASSET_CREATOR_ROLES: AppRole[] = [
  "super_admin",
  "biomed_head",
  "facility_manager",
];

// Roles allowed to create an asset via the GRN "Save & Create Asset" flow.
export const ASSET_GRN_CREATOR_ROLES: AppRole[] = [
  "super_admin",
  "biomed_head",
  "facility_manager",
  "purchase_stores",
];

// NOTE: These role-only helpers remain for backwards compatibility and for
// list/index pages where the asset category is not in scope. For per-asset
// decisions (edit/delete on a specific asset), prefer the entity-aware
// helpers in src/lib/permissions.ts (canEditAsset, canDeleteAsset).
export function canCreateAsset(roles: AppRole[]): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_asset"); }

  return roles.some((r) => ASSET_CREATOR_ROLES.includes(r));
}

export function canCreateAssetFromGRN(roles: AppRole[]): boolean {
  return roles.some((r) => ASSET_GRN_CREATOR_ROLES.includes(r));
}

// Edit access remains broader than create. Purchase Stores edits its own
// fields via the per-field purchase panel. Finance is intentionally excluded
// from asset master edits — finance interacts with assets only through the
// Finance module (capitalisation / depreciation / disposal accounting).
export function canEditAsset(roles: AppRole[]): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("edit_asset"); }

  return roles.some((r) =>
    ["super_admin", "biomed_head", "facility_manager", "purchase_stores"].includes(r),
  );
}

export function canDeleteAsset(roles: AppRole[]): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("delete_asset"); }

  return roles.includes("super_admin");
}

// ============== Asset onboarding lifecycle (post-GRN) =====================
// Drives the three handoff stages: technical verification → finance
// capitalisation → installation & commissioning. Each helper mirrors the
// RPC-side permission guard in the supabase migration so the UI hides
// actions the server would reject.

export type AssetOnboardingShape = {
  source_grn_id?: string | null;
  onboarding_stage?: string | null;
  technical_verified_at?: string | null;
  finance_capitalized_at?: string | null;
  installation_completed_at?: string | null;
};

export function canVerifyAssetTechnical(roles: AppRole[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "biomed_head" || r === "facility_manager");
}

export function canCapitaliseAsset(roles: AppRole[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "finance");
}

export function canRecordAssetInstallation(roles: AppRole[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "biomed_head" || r === "facility_manager");
}

const ALLOCATOR_BASE: AppRole[] = ["super_admin", "biomed_head", "facility_manager", "purchase_stores"];

export function isAssetOnboardingComplete(asset: AssetOnboardingShape | null | undefined): boolean {
  if (!asset) return false;
  if (asset.onboarding_stage === "completed" || asset.onboarding_stage === "ready_for_allocation") return true;
  return !!(asset.technical_verified_at && asset.finance_capitalized_at && asset.installation_completed_at);
}

// Relaxed allocation gate — role + not disposed/condemned + allocation slot
// available + class supports allocation. Onboarding-complete stays as a
// separate soft-gate (isAssetOnboardingComplete) that Asset Detail uses to
// render a disabled "Allocate" button with an explanatory tooltip.
export function canAllocateAsset(
  roles: AppRole[],
  asset:
    | (AssetOnboardingShape & {
        status?: string | null;
        allocation_status?: string | null;
        asset_class?: string | null;
        asset_type?: string | null;
      })
    | null
    | undefined,
): boolean {
  if (!roles.some((r) => ALLOCATOR_BASE.includes(r))) return false;
  if (!asset) return false;
  if (asset.status === "Disposed" || asset.status === "Condemned") return false;
  if (asset.allocation_status === "Allocated") return false;
  // Buildings/Infrastructure are not routinely allocated.
  if (asset.asset_class === "Infrastructure" || asset.asset_type === "Building") return false;
  return true;
}

// Phase B: Assign to internal user / third party / department.
// Same role base as allocation, but does not require onboarding to be complete
// (an asset can be sent to a vendor for install/calibration before allocation).
export function canAssignAsset(
  roles: AppRole[],
  asset: (AssetOnboardingShape & { status?: string | null }) | null | undefined,
): boolean {
  if (!roles.some((r) => ALLOCATOR_BASE.includes(r))) return false;
  if (asset?.status === "Disposed" || asset?.status === "Condemned") return false;
  return true;
}

export function canReturnAssetAssignment(roles: AppRole[]): boolean {
  return roles.some((r) => ALLOCATOR_BASE.includes(r));
}

// Top-level Edit. Purchase / Stores is excluded for GRN-sourced assets — they
// keep the Purchase panel for source-field edits instead. Finance is excluded
// entirely — finance writes happen via the Finance module workflows only.
export function canEditAssetFull(roles: AppRole[], asset: AssetOnboardingShape | null | undefined): boolean {
  const isGrnSourced = !!asset?.source_grn_id;
  return roles.some((r) => {
    if (r === "super_admin" || r === "biomed_head" || r === "facility_manager") return true;
    if (r === "purchase_stores") return !isGrnSourced;
    return false;
  });
}

// Purchase-only panel (PO / invoice / supplier). Locks once finance has
// capitalised the asset.
export function canEditAssetPurchase(roles: AppRole[], asset: AssetOnboardingShape | null | undefined): boolean {
  if (!roles.some((r) => r === "super_admin" || r === "purchase_stores" || r === "finance")) return false;
  if (asset?.finance_capitalized_at) return roles.includes("super_admin");
  return true;
}

export function onboardingStageLabel(asset: AssetOnboardingShape | null | undefined): string | null {
  if (!asset) return null;
  switch (asset.onboarding_stage) {
    case "technical_verification": return "Pending Technical Verification";
    case "finance_capitalisation": return "Pending Capitalisation";
    case "installation_commissioning": return "Pending Installation";
    case "ready_for_allocation": return "Ready for Allocation";
    case "completed": return null;
    default: return null;
  }
}

export function isHodOf(
  me: { hodDepartmentIds?: string[] } | null | undefined,
  deptId: string | null | undefined,
): boolean {
  if (!me || !deptId) return false;
  return (me.hodDepartmentIds ?? []).includes(deptId);
}

// True when the only meaningful read scope is the user's own department —
// i.e. dept_user / HOD with no cross-department role. Drives client-side
// dept filters on Assets and Work Orders (RLS still enforces server-side).
export function isDeptScopedOnly(roles: AppRole[] | undefined | null): boolean {
  const r = roles ?? [];
  if (r.length === 0) return false;
  const broad: AppRole[] = [
    "super_admin", "biomed_head", "biomed_engineer", "facility_manager",
    "finance", "purchase_stores", "quality", "management",
  ];
  if (r.some((x) => broad.includes(x))) return false;
  return r.includes("dept_user");
}



export function isAnyHod(
  me: { hodDepartmentIds?: string[] } | null | undefined,
): boolean {
  return (me?.hodDepartmentIds?.length ?? 0) > 0;
}

export function canCreateAudit(roles: AppRole[]): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_physical_audit"); }
  return roles.some((r) => r === "super_admin" || r === "biomed_head" || r === "biomed_engineer");
}

export function canSignoffAudit(roles: AppRole[]): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("signoff_physical_audit"); }
  return roles.some((r) => r === "super_admin" || r === "biomed_head");
}

export function isAssignedAuditor(audit: { assigned_auditors?: string[] } | null | undefined, userId: string | null | undefined): boolean {
  if (!audit || !userId) return false;
  return (audit.assigned_auditors ?? []).includes(userId);
}

export type DashboardVariant = "admin" | "management" | "biomed" | "finance" | "department" | "purchase" | "quality";

export function dashboardVariantsForRoles(roles: AppRole[]): DashboardVariant[] {
  if (roles.includes("super_admin")) {
    return ["admin", "management", "biomed", "finance", "purchase", "quality", "department"];
  }
  const out: DashboardVariant[] = [];
  if (roles.includes("management")) out.push("management");
  if (roles.some((r) => r === "biomed_head" || r === "biomed_engineer" || r === "facility_manager")) out.push("biomed");
  if (roles.includes("finance")) out.push("finance");
  if (roles.includes("purchase_stores")) out.push("purchase");
  if (roles.includes("quality")) out.push("quality");
  if (roles.includes("dept_user")) out.push("department");
  if (out.length === 0) out.push("department");
  return out;
}

export function defaultDashboardVariant(roles: AppRole[]): DashboardVariant {
  if (roles.includes("super_admin")) return "admin";
  if (roles.includes("management")) return "management";
  if (roles.includes("biomed_head")) return "biomed";
  if (roles.includes("biomed_engineer") || roles.includes("facility_manager")) return "biomed";
  if (roles.includes("finance")) return "finance";
  if (roles.includes("purchase_stores")) return "purchase";
  if (roles.includes("quality")) return "quality";
  if (roles.includes("dept_user")) return "department";
  return "department";
}

// =============== Branch switcher gating (MVP) ===============
// Only Super Admin and Management may switch branches via the top bar.
// All other roles are pinned to their assigned profile.branch_id.
export const BRANCH_SWITCHER_ROLES: AppRole[] = ["super_admin", "management"];

export function canSwitchBranches(roles: AppRole[] | undefined | null): boolean {
  return (roles ?? []).some((r) => BRANCH_SWITCHER_ROLES.includes(r));
}

export function isBranchLocked(roles: AppRole[] | undefined | null): boolean {
  return !canSwitchBranches(roles);
}






// =============== Phase D: Breakdown reporting ===============
export function canReportAssetBreakdown(_roles: AppRole[] | undefined | null): boolean {
  // All authenticated roles may report breakdowns.
  return true;
}

export function canViewOwnReportedBreakdowns(_roles: AppRole[] | undefined | null): boolean {
  return true;
}

const TRIAGE_ROLES: AppRole[] = ["super_admin", "biomed_head", "biomed_engineer", "facility_manager", "quality"];
export function canTriageBreakdown(roles: AppRole[] | undefined | null): boolean {
  return (roles ?? []).some((r) => TRIAGE_ROLES.includes(r));
}

const CROSS_DEPT_VIEW_ROLES: AppRole[] = [...TRIAGE_ROLES, "management"];
export function canViewCrossDepartmentReports(roles: AppRole[] | undefined | null): boolean {
  return (roles ?? []).some((r) => CROSS_DEPT_VIEW_ROLES.includes(r));
}

// =============== Phase E: Report/list export permissions ===============
// RBAC Phase 3 is not fully enforced yet, so these are soft role-based
// gates. Server-side RLS already scopes the underlying data by branch /
// department, so an authorised user can only export what they can see.
function any(roles: AppRole[] | undefined | null, allowed: AppRole[]): boolean {
  return (roles ?? []).some((r) => allowed.includes(r));
}

export function canExportPurchaseReports(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("export_purchase_indents"); }

  return any(roles, ["super_admin", "management", "purchase_stores", "finance", "biomed_head", "facility_manager"]);
}
export function canExportGrnReports(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("export_grn"); }

  return any(roles, ["super_admin", "management", "purchase_stores", "biomed_head", "facility_manager"]);
}
export function canExportAssetReports(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("export_assets"); }

  return any(roles, ["super_admin", "management", "biomed_head", "biomed_engineer", "facility_manager", "finance", "purchase_stores", "quality", "dept_user"]);
}
export function canExportMaintenanceReports(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("export_pm"); }

  return any(roles, ["super_admin", "management", "biomed_head", "biomed_engineer", "facility_manager", "quality"]);
}
export function canExportFinanceReports(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("export_finance"); }

  return any(roles, ["super_admin", "management", "finance"]);
}
export function canExportSpareReports(roles: AppRole[] | undefined | null): boolean {
  return any(roles, ["super_admin", "management", "purchase_stores", "biomed_head", "facility_manager", "biomed_engineer"]);
}
export function canExportContractReports(roles: AppRole[] | undefined | null): boolean {
  return any(roles, ["super_admin", "management", "biomed_head", "facility_manager", "finance", "purchase_stores"]);
}
export function canExportInsuranceReports(roles: AppRole[] | undefined | null): boolean {
  return any(roles, ["super_admin", "management", "finance"]);
}

// =============== Phase 1 Unified Ticketing permissions ===============
export function canCreateTicket(_roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  // If the system has assigned granular permissions to this user, use them.
  if (__perms && __perms.length > 0) { return __perms.includes("create_ticket"); }
  // Fall back: any authenticated user with a classic role may create tickets.
  return true;
}
export function canViewTicket(_roles: AppRole[] | undefined | null): boolean {
  return true; // page access; row-level enforced by RLS
}
export function canViewOwnTickets(_roles: AppRole[] | undefined | null): boolean {
  return true;
}
export function canViewDepartmentTickets(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("view_tickets"); }
  return any(roles, ["super_admin", "management", "biomed_head", "biomed_engineer", "facility_manager", "quality", "dept_user"]);
}
export function canViewAllBranchTickets(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("view_tickets"); }
  return any(roles, ["super_admin", "management", "biomed_head", "biomed_engineer", "facility_manager", "quality"]);
}
export function canCancelTicket(roles: AppRole[] | undefined | null): boolean {
  // Cancel is allowed for the ticket's reporter (enforced server-side) or these roles.
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("cancel_ticket"); }
  return any(roles, ["super_admin", "management", "biomed_head", "facility_manager"]);
}

// =============== Phase 2 Unified Ticketing lifecycle permissions ===============
const TICKET_MANAGERS: AppRole[] = ["super_admin", "biomed_head", "biomed_engineer", "facility_manager"];
const TICKET_ASSIGNERS: AppRole[] = ["super_admin", "biomed_head", "facility_manager"];
const TICKET_VERIFIERS: AppRole[] = ["super_admin", "biomed_head", "facility_manager", "quality"];

export function canAssignTicket(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("assign_ticket"); }
  return any(roles, TICKET_ASSIGNERS);
}
export function canReassignTicket(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("assign_ticket"); }
  return any(roles, TICKET_ASSIGNERS);
}
export function canUpdateTicketStatus(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  return any(roles, TICKET_MANAGERS);
}
export function canAddInternalNote(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  return any(roles, [...TICKET_MANAGERS, "quality"]);
}
export function canCloseTicket(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("close_ticket"); }
  return any(roles, TICKET_VERIFIERS);
}
export function canReopenTicket(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("reopen_ticket"); }
  return any(roles, TICKET_VERIFIERS);
}
export function canViewTicketTimeline(_roles: AppRole[] | undefined | null): boolean {
  return true;
}
export function canViewInternalTicketNotes(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  return any(roles, [...TICKET_MANAGERS, "quality", "management"]);
}

// =============== Phase 4 Spare request permissions ===============
const SPARE_APPROVERS: AppRole[] = ["super_admin", "biomed_head", "purchase_stores"];

export function canRequestSpare(roles: AppRole[] | undefined | null): boolean {
  // Anyone who can view/act on a ticket may request. Server enforces ticket visibility.
  return any(roles, [
    "super_admin", "biomed_head", "biomed_engineer", "facility_manager",
    "purchase_stores", "quality", "dept_user",
  ]);
}
export function canApproveSpareRequest(roles: AppRole[] | undefined | null): boolean {
  return any(roles, SPARE_APPROVERS);
}
export function canIssueSpare(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("issue_spare"); }

  return any(roles, SPARE_APPROVERS);
}
export function canMarkSparePurchaseRequired(roles: AppRole[] | undefined | null): boolean {
  return any(roles, [...SPARE_APPROVERS, "biomed_engineer"]);
}

// Phase 5: create a purchase indent from a spare request. Engineers cannot.
export function canCreateSpareIndent(roles: AppRole[] | undefined | null): boolean {
  return any(roles, SPARE_APPROVERS);
}

// =============== Phase 8: Ticket ↔ Work Order permissions ===============
export function canCreateWorkOrderFromTicket(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_work_order"); }

  return any(roles, ["super_admin", "biomed_head", "biomed_engineer", "facility_manager"]);
}
export function canViewLinkedWorkOrder(_roles: AppRole[] | undefined | null): boolean {
  return true;
}
export function canCompleteWorkOrder(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("close_work_order"); }

  return any(roles, ["super_admin", "biomed_head", "biomed_engineer", "facility_manager"]);
}
export function canCancelWorkOrder(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("close_work_order"); }

  return any(roles, ["super_admin", "biomed_head", "facility_manager"]);
}


// =============== Phase 9: Ticket-aware action helpers ===============
// These take a ticket + userId and compute per-ticket permission. They
// mirror the server-side matrix in public.user_can_act_on_ticket. UI must
// call these AND the server must re-check — never rely on button hiding.

type TicketLike = {
  reported_by?: string | null;
  created_by?: string | null;
  assigned_user_id?: string | null;
  assigned_department_id?: string | null;
  status?: string | null;
};

type ProfileLike = { id?: string | null; department_id?: string | null } | null | undefined;

const TICKET_MANAGERS_9: AppRole[] = ["super_admin", "biomed_head", "facility_manager"];
const TICKET_RESOLVER_ROLES: AppRole[] = ["super_admin", "biomed_head", "facility_manager", "biomed_engineer"];
const TICKET_VERIFIER_ROLES: AppRole[] = ["super_admin", "biomed_head", "facility_manager", "quality"];

export function isTicketReporter(ticket: TicketLike | null | undefined, userId: string | null | undefined): boolean {
  if (!ticket || !userId) return false;
  return ticket.reported_by === userId || ticket.created_by === userId;
}
export function isTicketAssignee(ticket: TicketLike | null | undefined, userId: string | null | undefined): boolean {
  if (!ticket || !userId) return false;
  return ticket.assigned_user_id === userId;
}
export function isTicketDeptMember(ticket: TicketLike | null | undefined, profile: ProfileLike): boolean {
  if (!ticket || !profile?.department_id || !ticket.assigned_department_id) return false;
  return ticket.assigned_department_id === profile.department_id;
}
function isManager(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  return any(roles, TICKET_MANAGERS_9);
}
function isResolver(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  if (any(roles, TICKET_RESOLVER_ROLES)) return true;
  if (isTicketAssignee(ticket, userId)) return true;
  if (isTicketDeptMember(ticket, profile) && any(roles, ["biomed_engineer", "facility_manager"])) return true;
  return false;
}

export function canCancelOwnTicket(roles: AppRole[] | undefined | null, ticket: TicketLike, userId: string | null | undefined): boolean {
  if (isManager(roles)) return true;
  if (!isTicketReporter(ticket, userId)) return false;
  return ticket.status === "New" || ticket.status === "Triage Pending";
}
export function canStartDiagnosisT(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canStartWorkT(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canPutTicketOnHold(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canResumeTicket(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canMarkWaitingForSpare(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canMarkWaitingForVendor(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canMarkWaitingForPurchase(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  return isResolver(roles, ticket, profile, userId);
}
export function canMarkTicketResolved(roles: AppRole[] | undefined | null, ticket: TicketLike, profile: ProfileLike, userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  if (isManager(roles) || any(roles, ["biomed_engineer"])) return true;
  return isTicketAssignee(ticket, userId) || (isTicketDeptMember(ticket, profile) && any(roles, ["biomed_engineer", "facility_manager"]));
}
export function canVerifyTicket(roles: AppRole[] | undefined | null, _ticket: TicketLike, _userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("close_ticket"); }

  return any(roles, TICKET_VERIFIER_ROLES) || any(roles, ["biomed_engineer"]);
}
export function canConfirmCloseTicket(roles: AppRole[] | undefined | null, ticket: TicketLike, userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0 && __perms.includes("close_ticket")) return true;
  if (any(roles, TICKET_VERIFIER_ROLES)) return true;
  return isTicketReporter(ticket, userId) && ticket.status === "Verification Pending";
}
export function canReopenTicketT(roles: AppRole[] | undefined | null, ticket: TicketLike, userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0 && __perms.includes("reopen_ticket")) return true;
  if (any(roles, TICKET_VERIFIER_ROLES)) return true;
  return isTicketReporter(ticket, userId) && ticket.status === "Closed";
}
export function canMarkTicketDuplicate(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  return isManager(roles);
}
export function canAddInternalTicketNote(roles: AppRole[] | undefined | null, ticket: TicketLike, userId: string | null | undefined): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("update_ticket_status"); }
  if (any(roles, [...TICKET_MANAGERS_9, "biomed_engineer", "quality"])) return true;
  return isTicketAssignee(ticket, userId);
}
export function canAddPublicTicketComment(_roles: AppRole[] | undefined | null): boolean { return true; }


// ============== Purchase Indent → PO flow gates =====================
// Only Finance (and super_admin) may accept an indent for purchase.
// Biomedical Head / Facility Manager stop at technical approval.
export function canAcceptForPurchase(roles: AppRole[] | undefined | null): boolean {
  if (!roles) return false;
  return roles.some((r) => r === "finance" || r === "super_admin");
}

// Purchase / Stores creates the PO after finance acceptance.
export function canCreatePOFromIndent(roles: AppRole[] | undefined | null): boolean {
  if (!roles) return false;
  return roles.some((r) => r === "purchase_stores" || r === "super_admin");
}

// ============== Module-level permission guards (for route beforeLoad) =====================

export function canCreateSpare(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_spare"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);
}

export function canCreateTransfer(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_transfer"); }
  return any(roles, ["super_admin", "biomed_head", "biomed_engineer", "facility_manager", "purchase_stores", "dept_user"]);
}

export function canCreateGrn(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_grn"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);
}

export function canCreatePurchaseIndent(roles: AppRole[] | undefined | null): boolean {
  // Mirrors backend CanCreatePurchaseIndent: department users raise Store Indents only (backend is the source of truth).
  if (!(roles ?? []).some((r) => (["super_admin", "purchase_stores", "procurement_officer", "procurement_head"] as string[]).includes(r))) return false;
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_purchase_indent"); }
  return true;
}

export function canCreateContract(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_contract"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "finance", "purchase_stores"]);
}

export function canCreateInsurancePolicy(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_policy"); }
  return any(roles, ["super_admin", "finance", "management"]);
}

export function canCreateLicence(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_licence"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "quality"]);
}

export function canCreateCondemnation(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_condemnation"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "biomed_engineer", "finance"]);
}

export function canCreateDisposal(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_disposal"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "finance", "purchase_stores"]);
}

export function canRunDepreciation(roles: AppRole[] | undefined | null): boolean {
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("run_depreciation"); }
  return any(roles, ["super_admin", "finance"]);
}

export function canCreateRfq(roles: AppRole[] | undefined | null): boolean {
  // RFQs are created from indents; same roles that handle procurement.
  const __perms = useAuthStore.getState().permissions;
  if (__perms && __perms.length > 0) { return __perms.includes("create_purchase_indent"); }
  return any(roles, ["super_admin", "biomed_head", "facility_manager", "purchase_stores", "finance", "management"]);
}
